// ---------------------------------------------------------------------------
// D30: Canned Response Manager
// ---------------------------------------------------------------------------
// CRUD for canned responses. Search by title/category. Create/edit/delete.
// Shortcut preview. Usage count tracking.

import { useState } from "react";
import { Plus, Search, Edit2, Trash2, MessageSquare, X } from "lucide-react";
import { useCannedResponses, createCannedResponse, updateCannedResponse, deleteCannedResponse } from "@/hooks/use-canned-responses";
import type { CannedResponse } from "@/hooks/use-canned-responses";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonCard } from "@/components/skeleton";

export default function CannedResponsesPage() {
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const { responses, isLoading, error, refetch } = useCannedResponses(search);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Canned Responses</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-press flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ background: "var(--gradient-energy)" }}
        >
          <Plus className="h-4 w-4" />
          New Response
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setFeedback(null);
          }}
          placeholder="Search by title or shortcut..."
          className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
        />
      </div>

      {(error || feedback) && (
        <div
          className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
          role="alert"
        >
          {feedback ?? error}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-20" />
          ))}
        </div>
      ) : responses.length === 0 ? (
        <EmptyState
          icon="inbox"
          title="No canned responses"
          description="Create reusable message templates to speed up your replies."
          actionLabel="Create first response"
          onAction={() => setShowCreate(true)}
        />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {responses.map((r) => (
            <div key={r.id} className="stagger-item flex items-start gap-3 p-4 transition-colors hover:bg-[var(--color-muted)]/30">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{r.title}</p>
                  {r.shortcut && (
                    <code className="rounded bg-[var(--color-muted)] px-1.5 py-0.5 text-[10px] text-muted-foreground">/{r.shortcut}</code>
                  )}
                  {r.category && (
                    <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] text-[var(--color-primary)]">{r.category}</span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{r.body}</p>
                <p className="mt-1 text-[10px] text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                  Used {r.usageCount} times · {r.isShared ? "Shared" : "Personal"}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditingId(r.id)}
                  className="rounded p-1.5 text-muted-foreground hover:text-foreground hover:bg-[var(--color-muted)] transition-colors"
                  aria-label="Edit"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm(`Delete "${r.title}"?`)) {
                      try {
                        await deleteCannedResponse(r.id);
                        setFeedback(null);
                        await refetch();
                      } catch (caughtError) {
                        setFeedback(caughtError instanceof Error ? caughtError.message : "Failed to delete canned response.");
                      }
                    }
                  }}
                  className="rounded p-1.5 text-muted-foreground hover:text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {(showCreate || editingId) && (
        <CannedResponseModal
          response={editingId ? responses.find((r) => r.id === editingId) ?? null : null}
          onClose={() => { setShowCreate(false); setEditingId(null); }}
          onSaved={async () => {
            setShowCreate(false);
            setEditingId(null);
            setFeedback(null);
            await refetch();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create/Edit Modal
// ---------------------------------------------------------------------------

function CannedResponseModal({
  response,
  onClose,
  onSaved,
}: {
  response: CannedResponse | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEditing = response !== null;
  const [title, setTitle] = useState(response?.title ?? "");
  const [body, setBody] = useState(response?.body ?? "");
  const [category, setCategory] = useState(response?.category ?? "");
  const [shortcut, setShortcut] = useState(response?.shortcut ?? "");
  const [isShared, setIsShared] = useState(response?.isShared ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setIsSaving(true);
    setSubmitError(null);
    try {
      if (isEditing && response) {
        await updateCannedResponse(response.id, {
          title: title.trim(),
          body: body.trim(),
          category: category.trim() || null,
          shortcut: shortcut.trim() || null,
          isShared,
        });
      } else {
        await createCannedResponse({
          title: title.trim(),
          body: body.trim(),
          category: category.trim() || undefined,
          shortcut: shortcut.trim() || undefined,
          isShared,
        });
      }
      await onSaved();
    } catch (caughtError) {
      setSubmitError(caughtError instanceof Error ? caughtError.message : "Failed to save canned response.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[4px]" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl animate-in zoom-in-95 fade-in duration-250"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">
              {isEditing ? "Edit Response" : "New Canned Response"}
            </h2>
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 px-6 py-4">
            {submitError && (
              <div
                className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
                role="alert"
              >
                {submitError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
              <input
                type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                placeholder="e.g., Welcome message"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Body * <span className="text-xs text-muted-foreground ml-1">Use {"{{contact.first_name}}"} for variables</span>
              </label>
              <textarea
                value={body} onChange={(e) => setBody(e.target.value)} required rows={5}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                placeholder="Hi {{contact.first_name}}, thanks for reaching out..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                <input
                  type="text" value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                  placeholder="e.g., greeting"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Shortcut</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">/</span>
                  <input
                    type="text" value={shortcut} onChange={(e) => setShortcut(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                    className="w-full rounded-lg border border-border bg-background pl-6 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                    placeholder="welcome"
                  />
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)} className="rounded" />
              <span className="text-foreground">Shared with team</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted">Cancel</button>
            <button
              type="submit" disabled={isSaving || !title.trim() || !body.trim()}
              className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: "var(--gradient-energy)" }}
            >
              {isSaving ? "Saving..." : isEditing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
